/**
 * Przekazywanie zdarzeń kółka myszy między rodzeństwem layoutu (VP-178).
 *
 * Widok czatu to flex-kolumna: scrollowalny obszar wiadomości + stała stopka
 * (input/konfigurator). Przeglądarka propaguje scroll wyłącznie do przodków,
 * więc kółko nad stopką, która nie ma własnego overflow, nie przewija niczego.
 * Ten moduł decyduje, kiedy zdarzenie wheel należy przekazać do obszaru
 * wiadomości: tylko gdy żaden element w łańcuchu DOM od celu zdarzenia aż do
 * korzenia dokumentu nie jest w stanie sam skonsumować scrolla w danym
 * kierunku (React rejestruje wheel pasywnie, więc preventDefault nie działa —
 * forward przy scrollowalnym przodku oznaczałby podwójne przewijanie).
 *
 * Czysta logika (canConsumeWheel, wheelDeltaToPixels) jest oddzielona od DOM,
 * żeby dało się ją testować w node'owym harnessie Vitest.
 */

export type ScrollMetrics = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  overflowY: string;
};

/**
 * Czy element o podanych metrykach sam skonsumuje scroll o zadanym deltaY?
 * Konsumuje, gdy jest scrollowalny (overflow auto/scroll), ma overflow
 * i nie dobił jeszcze do granicy w kierunku ruchu.
 */
export function canConsumeWheel(metrics: ScrollMetrics, deltaY: number): boolean {
  const scrollable = metrics.overflowY === "auto" || metrics.overflowY === "scroll";
  if (!scrollable) return false;
  if (metrics.scrollHeight <= metrics.clientHeight) return false;
  if (deltaY < 0) return metrics.scrollTop > 0;
  // -1: tolerancja na subpiksele (przeglądarki raportują ułamkowe scrollTop).
  return metrics.scrollTop + metrics.clientHeight < metrics.scrollHeight - 1;
}

/** Umowna wysokość linii dla deltaMode=DOM_DELTA_LINE (Firefox, część myszy). */
const LINE_HEIGHT_PX = 16;

/**
 * Przelicza deltę zdarzenia wheel na piksele. Chrome raportuje piksele
 * (deltaMode=0), ale Firefox i część myszy raportują linie (1) lub strony (2)
 * — surowe deltaY (~3 na tick) potraktowane jak piksele daje scroll
 * praktycznie niewidoczny.
 */
export function wheelDeltaToPixels(args: {
  deltaY: number;
  deltaMode: number;
  pageHeightPx: number;
}): number {
  if (args.deltaMode === 1) return args.deltaY * LINE_HEIGHT_PX;
  if (args.deltaMode === 2) return args.deltaY * args.pageHeightPx;
  return args.deltaY;
}

function elementCanConsumeWheel(el: HTMLElement, deltaY: number): boolean {
  // Tani test layoutu przed getComputedStyle — wheel odpala się 60–120×/s
  // (trackpad), a wymuszanie stylów na każdym przodku powodowałoby jank.
  if (el.scrollHeight <= el.clientHeight) return false;
  return canConsumeWheel(
    {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
      overflowY: window.getComputedStyle(el).overflowY,
    },
    deltaY
  );
}

/**
 * Czy zdarzenie wheel należy przekazać do innego kontenera? Zwraca false, gdy
 * cokolwiek w łańcuchu DOM od celu do korzenia dokumentu samo obsłuży scroll —
 * wtedy zostawiamy natywne zachowanie (forward dublowałby przewijanie).
 * Target spoza poddrzewa granicy (np. portal) nigdy nie jest przekazywany.
 */
export function shouldForwardWheel(args: {
  target: HTMLElement;
  boundary: HTMLElement;
  deltaY: number;
}): boolean {
  if (args.deltaY === 0) return false;

  let el: HTMLElement | null = args.target;
  let insideBoundary = false;
  while (el) {
    if (el === args.boundary) insideBoundary = true;
    if (elementCanConsumeWheel(el, args.deltaY)) return false;
    el = el.parentElement;
  }
  return insideBoundary;
}
